// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — Module Registry
// Each module maps to a route and set of Ignatius tables
// ═══════════════════════════════════════════════════════════════

import type { HubModule } from '../types';
import { TABLES } from '../api/tables';
import { primary, semantic } from './colors';

export const MODULES: HubModule[] = [
  {
    id: 'hotwash',
    name: 'HotWash',
    description: 'After-action review & observations',
    icon: 'IconFlame',
    route: '/hotwash',
    color: '#D97706',
    tables: [TABLES.observations, TABLES.aiInsights],
  },
  {
    id: 'handoff',
    name: 'Handoff',
    description: 'Shift handoff & position tracking',
    icon: 'IconArrowsExchange',
    route: '/handoff',
    color: primary[700],
    tables: [TABLES.handoffEvents, TABLES.handoffPositions, TABLES.handoffNotes, TABLES.handoffActionItems],
  },
  {
    id: 'rollcall',
    name: 'RollCall',
    description: 'Personnel accountability',
    icon: 'IconClipboardCheck',
    route: '/rollcall',
    color: '#07963F',
    tables: [TABLES.rcPersonnel, TABLES.rcEvents, TABLES.rcCheckins],
  },
  {
    id: 'geoops',
    name: 'GeoOps',
    description: 'Incident geography & multi-zone management',
    icon: 'IconMap2',
    route: '/geoops',
    color: semantic.error,
    tables: [TABLES.geoopsZones],
  },
  {
    id: 'impact',
    name: 'Impact',
    description: 'Damage assessment & mapping',
    icon: 'IconHomeExclamation',
    route: '/impact',
    color: '#DF461B',
    tables: [TABLES.impactAssessments, TABLES.impactDetails, TABLES.impactPhotos, TABLES.impactSelfReports],
  },
  {
    id: 'intake',
    name: 'Intake',
    description: 'Individual intake & triage',
    icon: 'IconUserPlus',
    route: '/intake',
    color: '#0EA5E9',
    tables: [TABLES.individuals, TABLES.propertyItems],
  },
  {
    id: 'battlerhythm',
    name: 'Battle Rhythm',
    description: 'Operational tempo board',
    icon: 'IconCalendarEvent',
    route: '/battlerhythm',
    color: '#8B5CF6',
    tables: [TABLES.brMeta, TABLES.brEvents, TABLES.brTemplates],
  },
  {
    id: 'pace',
    name: 'PACE',
    description: 'Communications plan',
    icon: 'IconAntenna',
    route: '/pace',
    color: '#06B6D4',
    tables: [TABLES.paceProfiles, TABLES.pacePlans],
  },
  {
    id: 'volunteer',
    name: 'VolunteerOS',
    description: 'Volunteer management & scheduling',
    icon: 'IconHeartHandshake',
    route: '/volunteer',
    color: '#059669',
    tables: [TABLES.volunteers, TABLES.volunteerEvents, TABLES.volunteerAssignments],
  },
  {
    id: 'filelibrary',
    name: 'File Library',
    description: 'Document management',
    icon: 'IconFolderOpen',
    route: '/filelibrary',
    color: '#64748B',
    tables: [TABLES.flDocuments],
  },
  {
    id: 'blackboard',
    name: 'Blackboard',
    description: 'Training management & compliance',
    icon: 'IconChalkboard',
    route: '/blackboard',
    color: primary[900],
    tables: [TABLES.bbTrainings, TABLES.bbTrainingRecords, TABLES.bbClassrooms, TABLES.bbCertificates, TABLES.bbPositionPlans],
  },
  {
    id: 'incidents',
    name: 'Incident Hub',
    description: 'Incident tracking & management',
    icon: 'IconShieldCheck',
    route: '/incidents',
    color: '#B91C1C',
    tables: [TABLES.incidents, TABLES.activityLog],
  },
  {
    id: 'mainstreet',
    name: 'MainStreet',
    description: 'Business & Community Recovery',
    icon: 'IconBuildingCommunity',
    route: '/mainstreet',
    color: '#0891b2',
    tables: [
      TABLES.msOrganizations, TABLES.msStatusChecks, TABLES.msStatusResponses,
      TABLES.msDamageReports, TABLES.msResourceOffers, TABLES.msPosts,
      TABLES.msFacilities, TABLES.msContacts, TABLES.msOrgLists,
      TABLES.msReentryAuths, TABLES.msReentryPasses,
    ],
  },
  {
    id: 'debris',
    name: 'Debris',
    description: 'Automated Debris Management System',
    icon: 'IconBackhoe',
    route: '/debris',
    color: '#92400E',
    tables: [
      TABLES.debrisZones, TABLES.debrisContractors, TABLES.debrisCrews,
      TABLES.debrisTrucks, TABLES.debrisLoadTickets, TABLES.debrisDisposalSites,
      TABLES.debrisPhotos, TABLES.debrisDocuments,
      TABLES.debrisInvoices,
    ],
  },
  {
    id: 'shelter',
    name: 'Shelter',
    description: 'Shelter Management & Survivor Intake',
    icon: 'IconHomePlus',
    route: '/shelter',
    color: '#7C3AED',
    tables: [
      TABLES.shelters, TABLES.survivors, TABLES.shelterFamilies,
      TABLES.shelterPets, TABLES.shelterVehicles, TABLES.shelterMedicalLog,
      TABLES.shelterTransfers, TABLES.shelterIncidents, TABLES.shelterReferrals,
      TABLES.shelterSupplies, TABLES.shelterReunification,
    ],
  },
  {
    id: 'pagrants',
    name: 'Disaster Grants',
    description: 'FEMA PA & Hazard Mitigation Grant Management',
    icon: 'IconFileInvoice',
    route: '/pagrants',
    color: '#1E40AF',
    tables: [
      TABLES.paOrganizations, TABLES.paDisasters, TABLES.paDamageInventory,
      TABLES.paProjects, TABLES.paProjectCosts, TABLES.paForceAccountLabor,
      TABLES.paForceAccountEquip, TABLES.paMaterials, TABLES.paContracts,
      TABLES.paRentedEquipment, TABLES.paDonatedResources, TABLES.paInsurance,
      TABLES.paCompliance, TABLES.paDocuments, TABLES.paTimelineEvents, TABLES.paNotes,
    ],
  },
  {
    id: 'timelabor',
    name: 'Time & Labor',
    description: 'Force account labor tracking & FEMA FF-104 reporting',
    icon: 'IconClock',
    route: '/timelabor',
    color: '#0D9488',
    tables: [
      TABLES.laborEmployees, TABLES.forceAccountLabor,
    ],
  },
  {
    id: 'workflows',
    name: 'Workflows',
    description: 'Custom workflow engine — approvals, routing, tasks',
    icon: 'IconGitMerge',
    route: '/workflows',
    color: '#7C3AED',
    tables: [
      TABLES.workflowTemplates, TABLES.workflowInstances, TABLES.workflowSteps,
    ],
  },
  {
    id: 'forms',
    name: 'Forms',
    description: 'Custom form builder & data collection',
    icon: 'IconForms',
    route: '/forms',
    color: '#8B5CF6',
    tables: [
      TABLES.formTemplates, TABLES.formResponses,
    ],
  },
];

export const INCIDENT_TYPE_ICONS: Record<string, string> = {
  hurricane: 'IconCloudStorm',
  wildfire: 'IconFlame',
  flood: 'IconDroplet',
  earthquake: 'IconBuildingBridge',
  tornado: 'IconTornado',
  hazmat: 'IconBiohazard',
  exercise: 'IconClipboardCheck',
  planned: 'IconCalendar',
  general: 'IconShield',
  other: 'IconInfoCircle',
};
